import type {
  MediaItem,
  OpsFailure,
  OpsLoginResult,
  OpsOverview,
  OpsSessionDetail,
  SessionLimits,
  SessionPlan,
} from '@wallflower/shared';
import { ApiError, request } from './api.ts';

/**
 * Per-tab, so closing the tab signs the operator out, and never localStorage:
 * this credential reaches every session on the instance and has no business
 * surviving a reboot or being shared across windows.
 */
const TOKEN_KEY = 'wallfeed:ops-token';

interface StoredToken {
  token: string;
  expiresAt: number;
}

export function readOpsToken(): StoredToken | null {
  const raw = sessionStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as StoredToken;
    // The server enforces the real expiry; this only avoids a pointless round trip.
    if (!stored.token || stored.expiresAt <= Date.now()) {
      sessionStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return stored;
  } catch {
    sessionStorage.removeItem(TOKEN_KEY);
    return null;
  }
}

export function writeOpsToken(stored: StoredToken): void {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(stored));
}

export function clearOpsToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

/** Attaches the ops token, and drops it the moment the server stops accepting it. */
async function opsRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const stored = readOpsToken();
  try {
    return await request<T>(path, {
      ...init,
      headers: { ...init?.headers, ...(stored ? { 'x-ops-token': stored.token } : {}) },
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) clearOpsToken();
    throw err;
  }
}

export const opsApi = {
  login(passphrase: string): Promise<OpsLoginResult> {
    return request('/api/ops/login', {
      method: 'POST',
      body: JSON.stringify({ passphrase }),
    });
  },

  logout(): Promise<{ ok: true }> {
    return opsRequest('/api/ops/logout', { method: 'POST' });
  },

  overview(): Promise<OpsOverview> {
    return opsRequest('/api/ops/overview');
  },

  sessionDetail(sessionId: string): Promise<OpsSessionDetail> {
    return opsRequest(`/api/ops/sessions/${sessionId}`);
  },

  setExpiry(sessionId: string, expiresAt: number): Promise<{ expiresAt: number }> {
    return opsRequest(`/api/ops/sessions/${sessionId}/expiry`, {
      method: 'PATCH',
      body: JSON.stringify({ expiresAt }),
    });
  },

  /** A comp, a goodwill upgrade, or putting right a checkout that went astray. */
  setPlan(
    sessionId: string,
    plan: SessionPlan,
  ): Promise<{ plan: SessionPlan; limits: SessionLimits; expiresAt: number }> {
    return opsRequest(`/api/ops/sessions/${sessionId}/plan`, {
      method: 'POST',
      body: JSON.stringify({ plan }),
    });
  },

  deleteSession(sessionId: string): Promise<{ ok: true }> {
    return opsRequest(`/api/ops/sessions/${sessionId}`, { method: 'DELETE' });
  },

  failures(): Promise<{ failures: OpsFailure[] }> {
    return opsRequest('/api/ops/failures');
  },

  retryMedia(mediaId: string): Promise<{ media: MediaItem }> {
    return opsRequest(`/api/ops/media/${mediaId}/retry`, { method: 'POST' });
  },
};
